import React, { useState, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import OTPForm from 'features/auth/components/OTPForm';
import useAuth from 'features/auth/hooks/useAuth';
import { signIn, signUp, resendOTP } from 'features/auth/services/auth.service';
import { countdownHelper, handleSuccessfulLogin } from 'utils/helpers';
import { Button } from 'components/common/Button/Button';
import {
	AuthResponse,
	AuthType,
	LoginCredentials,
	RegisterCredentials
} from 'features/auth/types/auth.types';

export const OTPPage: React.FC = () => {
	const { state } = useLocation();
	const { login } = useAuth();
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [error, setError] = useState<boolean>(false);
	const [countdown, setCountdown] = useState<number>(60);

	const onMutationSuccess = (data: AuthResponse) => {
		login(data.token, data.refreshToken, () => {
			navigate(handleSuccessfulLogin());
		});
	};

	const signUpMutation = useMutation({
		mutationFn: (formData: RegisterCredentials) => signUp(formData),
		onSuccess: onMutationSuccess,
		onError: () => {
			setError(true);
		}
	});

	const signInMutation = useMutation({
		mutationFn: (formData: LoginCredentials) => signIn(formData),
		onSuccess: onMutationSuccess,
		onError: () => {
			setError(true);
		}
	});

	const resendOTPMutation = useMutation({
		mutationFn: () => resendOTP({ email: state?.formData?.email }),
		onSuccess: () => {
			setCountdown(60);
		}
	});

	const handleOTPSubmit = (otp: string) => {
		if (state.authType === AuthType.SIGNUP) {
			signUpMutation.mutate({
				...state.formData,
				phoneNumber: `+${state.formData.phoneNumber}`,
				otp
			});
		} else if (state.authType === AuthType.SIGNIN) {
			signInMutation.mutate({ ...state.formData, otp });
		}
	};

	const handleResendOTP = () => {
		resendOTPMutation.mutate();
	};

	useEffect(() => {
		countdownHelper(60, setCountdown, () => {
			undefined;
		});
	}, []);

	useEffect(() => {
		if (countdown === 60) {
			countdownHelper(60, setCountdown);
		}
	}, [countdown]);

	return (
		<div className='flex min-h-screen flex-col'>
			<main className='flex w-full flex-1 items-center justify-center'>
				<div className='h-full w-full px-4 md:px-8'>
					<p className='mb-8 text-center text-gray-600'>
						<Trans
							i18nKey='otp.enter_otp'
							values={{
								email: state?.formData?.email
							}}
						>
							<span className='font-semibold'></span>
						</Trans>
					</p>

					<OTPForm
						onSubmit={handleOTPSubmit}
						error={error}
						isLoading={
							signUpMutation.isLoading || signInMutation.isLoading
						}
					/>

					<div className='mt-4 flex justify-center'>
						{countdown > 0 ? (
							<p className='md py-4 text-gray-500'>
								<Trans
									i18nKey='otp.resend_available_in'
									values={{ countdown }}
								/>
							</p>
						) : (
							<Button
								variant='link'
								onClick={handleResendOTP}
								disabled={
									resendOTPMutation.isLoading || countdown > 0
								}
								className='md py-4'
							>
								{resendOTPMutation.isLoading
									? t('otp.resending')
									: t('otp.resend')}
							</Button>
						)}
					</div>
				</div>
			</main>
		</div>
	);
};

export default OTPPage;
